import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback } from "react";
import { ActionButton } from "@perfectserve/library";
import { faPenToSquare } from "@fortawesome/pro-solid-svg-icons";
const KPIDisplayBanner = React.memo(({ kpiResult, onPerformanceClick, filteredInteractions = [], onEditKPI }) => {
    if (!kpiResult) {
        return null;
    }
    const getInteractionsByThreshold = useCallback((thresholdType) => {
        if (!filteredInteractions.length)
            return [];
        return filteredInteractions.filter((interaction) => {
            if (!interaction.CallbackFromMessageIdUtc || !interaction.SecureMessageSentUtc) {
                return false;
            }
            try {
                const callbackTime = new Date(interaction.CallbackFromMessageIdUtc).getTime();
                const sentTime = new Date(interaction.SecureMessageSentUtc).getTime();
                const callbackTimeMinutes = Math.floor((callbackTime - sentTime) / (1000 * 60));
                switch (thresholdType) {
                    case "excellent":
                        return callbackTimeMinutes < kpiResult.kpiConfig.thresholds.excellent;
                    case "good":
                        return (callbackTimeMinutes >= kpiResult.kpiConfig.thresholds.excellent &&
                            callbackTimeMinutes < kpiResult.kpiConfig.thresholds.good);
                    case "acceptable":
                        return (callbackTimeMinutes >= kpiResult.kpiConfig.thresholds.good &&
                            callbackTimeMinutes < kpiResult.kpiConfig.thresholds.acceptable);
                    case "poor":
                        return callbackTimeMinutes >= kpiResult.kpiConfig.thresholds.acceptable;
                    default:
                        return false;
                }
            }
            catch (_a) {
                return false;
            }
        });
    }, [filteredInteractions, kpiResult]);
    if (kpiResult.totalInteractions > 0 && kpiResult.callbackInteractions === 0) {
        return (_jsxs("div", { className: "d-flex align-items-center justify-content-between p-10 bg-warning-light text-warning", style: {
                borderRadius: "8px",
                border: "1px solid #ffeaa7",
                margin: "0",
                fontSize: "12px",
            }, children: [_jsxs("span", { children: [_jsx("strong", { children: kpiResult.kpiConfig.name }), " - ", kpiResult.totalInteractions, " interactions found, but none have callback data"] }), onEditKPI && (_jsx(ActionButton, { icon: faPenToSquare, onClick: onEditKPI, title: "Edit KPI", size: "large", variant: "default" }))] }));
    }
    if (kpiResult.totalInteractions === 0) {
        return (_jsxs("div", { className: "d-flex align-items-center justify-content-between p-10 bg-danger-light text-danger", style: {
                borderRadius: "8px",
                border: "1px solid #f5c6cb",
                margin: "0",
                fontSize: "12px",
            }, children: [_jsxs("span", { children: [_jsx("strong", { children: kpiResult.kpiConfig.name }), " - No interactions match the selected filters"] }), onEditKPI && (_jsx(ActionButton, { icon: faPenToSquare, onClick: onEditKPI, title: "Edit KPI", size: "large", variant: "default" }))] }));
    }
    if (kpiResult.callbackInteractions === 0) {
        return null;
    }
    const { kpiConfig, excellentPct, goodPct, acceptablePct, poorPct } = kpiResult;
    return (_jsxs("div", { className: "d-flex align-items-center flex-wrap bg-gray-100 text-gray-800", style: {
            border: "1px solid var(--ps-gray4)",
            borderRadius: "4px",
            gap: "10px",
            fontSize: "15px",
            fontWeight: "600",
            margin: "0",
            padding: "8px 15px",
        }, children: [_jsx("span", { className: "ps-blue", style: { fontWeight: "bold" }, children: kpiConfig.name }), excellentPct > 0 && (_jsxs("span", { style: {
                    color: "#007bff",
                    cursor: onPerformanceClick ? "pointer" : "default",
                    textDecoration: "none",
                }, onClick: () => {
                    if (onPerformanceClick) {
                        const interactions = getInteractionsByThreshold("excellent");
                        onPerformanceClick(`< ${kpiConfig.thresholds.excellent}m`, interactions);
                    }
                }, onMouseEnter: (e) => {
                    if (onPerformanceClick) {
                        e.currentTarget.style.color = "#0056b3";
                        e.currentTarget.style.textDecoration = "underline";
                    }
                }, onMouseLeave: (e) => {
                    if (onPerformanceClick) {
                        e.currentTarget.style.color = "#007bff";
                        e.currentTarget.style.textDecoration = "none";
                    }
                }, children: [Math.round(excellentPct), "% < ", kpiConfig.thresholds.excellent, "m"] })), goodPct > 0 && (_jsxs("span", { style: {
                    color: "#007bff",
                    cursor: onPerformanceClick ? "pointer" : "default",
                    textDecoration: "none",
                }, onClick: () => {
                    if (onPerformanceClick) {
                        const interactions = getInteractionsByThreshold("good");
                        onPerformanceClick(`< ${kpiConfig.thresholds.good}m`, interactions);
                    }
                }, onMouseEnter: (e) => {
                    if (onPerformanceClick) {
                        e.currentTarget.style.color = "#0056b3";
                        e.currentTarget.style.textDecoration = "underline";
                    }
                }, onMouseLeave: (e) => {
                    if (onPerformanceClick) {
                        e.currentTarget.style.color = "#007bff";
                        e.currentTarget.style.textDecoration = "none";
                    }
                }, children: [Math.round(goodPct), "% < ", kpiConfig.thresholds.good, "m"] })), acceptablePct > 0 && (_jsxs("span", { style: {
                    color: "#007bff",
                    cursor: onPerformanceClick ? "pointer" : "default",
                    textDecoration: "none",
                }, onClick: () => {
                    if (onPerformanceClick) {
                        const interactions = getInteractionsByThreshold("acceptable");
                        onPerformanceClick(`< ${kpiConfig.thresholds.acceptable}m`, interactions);
                    }
                }, onMouseEnter: (e) => {
                    if (onPerformanceClick) {
                        e.currentTarget.style.color = "#0056b3";
                        e.currentTarget.style.textDecoration = "underline";
                    }
                }, onMouseLeave: (e) => {
                    if (onPerformanceClick) {
                        e.currentTarget.style.color = "#007bff";
                        e.currentTarget.style.textDecoration = "none";
                    }
                }, children: [Math.round(acceptablePct), "% < ", kpiConfig.thresholds.acceptable, "m"] })), poorPct > 0 && (_jsxs("span", { style: {
                    color: "var(--ps-danger)",
                    cursor: onPerformanceClick ? "pointer" : "default",
                    textDecoration: "none",
                }, onClick: () => {
                    if (onPerformanceClick) {
                        const interactions = getInteractionsByThreshold("poor");
                        onPerformanceClick(`> ${kpiConfig.thresholds.acceptable}m`, interactions);
                    }
                }, onMouseEnter: (e) => {
                    if (onPerformanceClick) {
                        e.currentTarget.style.color = "#c82333";
                        e.currentTarget.style.textDecoration = "underline";
                    }
                }, onMouseLeave: (e) => {
                    if (onPerformanceClick) {
                        e.currentTarget.style.color = "#dc3545";
                        e.currentTarget.style.textDecoration = "none";
                    }
                }, children: [Math.round(poorPct), "% > ", kpiConfig.thresholds.acceptable, "m"] })), onEditKPI && (_jsx("div", { style: { marginLeft: "auto" }, children: _jsx(ActionButton, { icon: faPenToSquare, onClick: onEditKPI, title: "Edit KPI", size: "large", variant: "default" }) }))] }));
});
KPIDisplayBanner.displayName = "KPIDisplayBanner";
export default KPIDisplayBanner;
//# sourceMappingURL=KPIDisplayBanner.js.map